import { ENV } from '@/lib/env';
import type { ApiEnvelope, ApiError } from './types';

/**
 * Лёгкий fetch-обёртка.
 *  - добавляет Bearer-токен из getToken()
 *  - кладёт Accept-Language
 *  - разворачивает {data, meta, error} envelope
 *  - на 401 пытается перелогиниться через onUnauthorized
 *  - на 5xx делает один retry
 */

type TokenGetter = () => string | null;

let getToken: TokenGetter = () => null;
let onUnauthorized: () => Promise<void> = async () => {};
let getLang: () => string = () => 'en';

// Дедупликация параллельных re-auth: один in-flight promise, остальные ждут его.
let pendingAuth: Promise<void> | null = null;
async function dedupedOnUnauthorized(): Promise<void> {
  if (pendingAuth) return pendingAuth;
  pendingAuth = (async () => {
    try { await onUnauthorized(); } finally { pendingAuth = null; }
  })();
  return pendingAuth;
}

export function configureApiClient(opts: {
  getToken: TokenGetter;
  onUnauthorized?: () => Promise<void>;
  getLang?: () => string;
}) {
  getToken = opts.getToken;
  if (opts.onUnauthorized) onUnauthorized = opts.onUnauthorized;
  if (opts.getLang) getLang = opts.getLang;
}

export class ApiException extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
  retryOn5xx?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<{ data: T; meta?: Record<string, unknown> }> {
  const url = `${ENV.API_BASE_URL}${path}`;
  const headers: Record<string, string> = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'Accept-Language': getLang(),
    ...((options.headers as Record<string, string>) ?? {}),
  };
  if (!options.skipAuth) {
    const t = getToken();
    if (t) headers.Authorization = `Bearer ${t}`;
  }

  const doFetch = () => fetch(url, { ...options, headers });

  let res: Response;
  try {
    res = await doFetch();
  } catch (e) {
    throw new ApiException(0, 'network', (e as Error).message);
  }

  if (res.status === 401 && !options.skipAuth) {
    try {
      await dedupedOnUnauthorized();
    } catch { /* fallthrough */ }
    const t = getToken();
    if (t) {
      headers.Authorization = `Bearer ${t}`;
      res = await fetch(url, { ...options, headers });
    }
  }

  if (res.status >= 500 && options.retryOn5xx !== false) {
    await new Promise(r => setTimeout(r, 300));
    res = await doFetch();
  }

  let body: ApiEnvelope<T> | null = null;
  try {
    body = await res.json();
  } catch {
    // не JSON
  }

  if (!res.ok || body?.error) {
    const err = body?.error ?? ({ code: 'http_error', message: res.statusText } as ApiError);
    throw new ApiException(res.status, err.code, err.message, err.details);
  }

  return { data: (body?.data as T) ?? (null as unknown as T), meta: body?.meta };
}

export const http = {
  get:    <T,>(path: string, opt: RequestOptions = {}) => request<T>(path, { ...opt, method: 'GET' }),
  post:   <T,>(path: string, body?: unknown, opt: RequestOptions = {}) =>
    request<T>(path, { ...opt, method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch:  <T,>(path: string, body?: unknown, opt: RequestOptions = {}) =>
    request<T>(path, { ...opt, method: 'PATCH', body: body !== undefined ? JSON.stringify(body) : undefined }),
  delete: <T,>(path: string, opt: RequestOptions = {}) => request<T>(path, { ...opt, method: 'DELETE' }),
};

// multipart/form-data — браузер сам поставит Content-Type с boundary.
async function requestForm<T>(path: string, form: FormData): Promise<{ data: T; meta?: Record<string, unknown> }> {
  const url = `${ENV.API_BASE_URL}${path}`;
  const headers: Record<string, string> = {
    'Accept': 'application/json',
    'Accept-Language': getLang(),
  };
  const t = getToken();
  if (t) headers.Authorization = `Bearer ${t}`;

  let res: Response;
  try {
    res = await fetch(url, { method: 'POST', headers, body: form });
  } catch (e) {
    throw new ApiException(0, 'network', (e as Error).message);
  }

  if (res.status === 401) {
    try { await dedupedOnUnauthorized(); } catch { /* ignore */ }
    const t2 = getToken();
    if (t2) {
      headers.Authorization = `Bearer ${t2}`;
      res = await fetch(url, { method: 'POST', headers, body: form });
    }
  }

  let body: ApiEnvelope<T> | null = null;
  try { body = await res.json(); } catch { /* not JSON */ }

  if (!res.ok || body?.error) {
    const err = body?.error ?? ({ code: 'http_error', message: res.statusText } as ApiError);
    throw new ApiException(res.status, err.code, err.message, err.details);
  }

  return { data: (body?.data as T) ?? (null as unknown as T), meta: body?.meta };
}

export const httpForm = {
  post: <T,>(path: string, form: FormData) => requestForm<T>(path, form),
};
